import React, { useState, useEffect } from 'react';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { useLang, SUPPORTED_LANGS, LANG_LABELS } from '../context/LanguageContext';

const PRODUCTS = [
  { id: 'care', name: 'InvictaCare', color: '#1E8E99', path: '/invictacare' },
  { id: 'fit', name: 'InvictaFit', color: '#249D68', path: '/invictafit' },
  { id: 'dance', name: 'InvictaDance', color: '#7B3FB8', path: '/invictadance' },
  { id: 'beauty', name: 'InvictaBeauty', color: '#A05A72', path: '/invictabeauty' },
  { id: 'dental', name: 'InvictaDental', color: '#0E7FAD', path: '/invictadental' },
  { id: 'auto', name: 'InvictaAuto', color: '#2A5C7E', path: '/invictaauto' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToPlatforms = () => {
    setMobileMenuOpen(false);
    navigate('/plattformen');
  };

  const scrollToProduct = (id) => {
    setSolutionsOpen(false);
    setMobileMenuOpen(false);
    const product = PRODUCTS.find((p) => p.id === id);
    navigate(product ? product.path : '/');
  };

  const navItemClass =
    'text-[#1D1D1F] hover:text-[#1D1D1F] text-sm opacity-80 hover:opacity-100 transition-opacity duration-200 whitespace-nowrap';
  const mobileItemClass =
    'text-[#1D1D1F] hover:text-[#1D1D1F] text-sm opacity-80 hover:opacity-100 transition-opacity text-left';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/80 backdrop-blur-2xl border-b border-[#D2D2D7]' : 'bg-white/60 backdrop-blur-xl'
      }`}
    >
      <div className="max-w-[980px] mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('hero')} data-testid="header-logo">
            <Logo />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 ml-8">
            <button onClick={() => scrollToSection('hero')} className={navItemClass}>
              {t('nav.home')}
            </button>

            {/* Solutions Dropdown */}
            <div className="relative">
              <button
                onClick={() => setSolutionsOpen(!solutionsOpen)}
                className={`${navItemClass} flex items-center gap-1`}
                data-testid="nav-solutions-dropdown"
              >
                {t('nav.solutions')}
                <ChevronDown size={13} className={`transition-transform ${solutionsOpen ? 'rotate-180' : ''}`} />
              </button>
              {solutionsOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setSolutionsOpen(false)} />
                  <div className="absolute left-0 top-8 z-50 bg-white rounded-2xl shadow-xl border border-[#D2D2D7] py-2 min-w-[190px]">
                    {PRODUCTS.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => scrollToProduct(p.id)}
                        className="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-sm text-[#1D1D1F] hover:bg-[#F5F5F7] transition-colors"
                        data-testid={`nav-solution-${p.id}`}
                      >
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                        {p.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <button onClick={() => scrollToSection('services')} className={navItemClass}>
              {t('nav.services')}
            </button>
            <button onClick={() => scrollToSection('projects')} className={navItemClass}>
              {t('nav.references')}
            </button>
            <button
              onClick={goToPlatforms}
              className={`${navItemClass} ${location.pathname === '/plattformen' ? 'opacity-100 font-semibold' : ''}`}
              data-testid="nav-platforms-link"
            >
              {t('nav.platforms')}
            </button>
            <button onClick={() => scrollToSection('pricing')} className={navItemClass}>
              {t('nav.pricing')}
            </button>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`${navItemClass} flex items-center gap-1`}
                data-testid="lang-switcher"
              >
                <Globe size={15} strokeWidth={1.8} />
                {lang.toUpperCase()}
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                  <div className="absolute right-0 top-8 z-50 bg-white rounded-2xl shadow-xl border border-[#D2D2D7] py-2 min-w-[150px]">
                    {SUPPORTED_LANGS.map((l) => (
                      <button
                        key={l}
                        onClick={() => { setLang(l); setLangOpen(false); }}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-[#F5F5F7] transition-colors ${
                          l === lang ? 'text-[#0071E3] font-semibold' : 'text-[#1D1D1F]'
                        }`}
                        data-testid={`lang-option-${l}`}
                      >
                        {LANG_LABELS[l]}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-[#0071E3] hover:bg-[#0077ED] text-white text-sm rounded-full px-5 h-9"
            >
              {t('nav.cta')}
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#1D1D1F]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 flex flex-col space-y-4 border-t border-[#D2D2D7] pt-4">
            <button onClick={() => scrollToSection('hero')} className={mobileItemClass}>
              {t('nav.home')}
            </button>

            <div>
              <p className="text-xs text-[#86868B] uppercase tracking-wide mb-2">{t('nav.solutions')}</p>
              <div className="flex flex-col space-y-2 pl-3">
                {PRODUCTS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => scrollToProduct(p.id)}
                    className={`${mobileItemClass} flex items-center gap-2`}
                    data-testid={`nav-solution-mobile-${p.id}`}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                    {p.name}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={() => scrollToSection('services')} className={mobileItemClass}>
              {t('nav.services')}
            </button>
            <button onClick={() => scrollToSection('projects')} className={mobileItemClass}>
              {t('nav.references')}
            </button>
            <button onClick={goToPlatforms} className={mobileItemClass} data-testid="nav-platforms-link-mobile">
              {t('nav.platforms')}
            </button>
            <button onClick={() => scrollToSection('pricing')} className={mobileItemClass}>
              {t('nav.pricing')}
            </button>

            <div className="flex flex-wrap gap-2 pt-1" data-testid="lang-switcher-mobile">
              {SUPPORTED_LANGS.map((l) => (
                <button
                  key={l}
                  onClick={() => { setLang(l); setMobileMenuOpen(false); }}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    l === lang
                      ? 'bg-[#0071E3] text-white border-[#0071E3]'
                      : 'border-[#D2D2D7] text-[#1D1D1F] hover:bg-[#F5F5F7]'
                  }`}
                  data-testid={`lang-option-mobile-${l}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full w-full"
            >
              {t('nav.cta')}
            </Button>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
